import { Card, CardFooter, Typography } from '@growdash/design-system/atoms';
import React from 'react';

const getNeedleColor = (percentage: number) => {
  if (percentage <= 0.33) {
    return '#5767CF';
  } else if (percentage <= 0.66) {
    return '#41A2F2';
  } else {
    return '#10CBD2';
  }
};

type Props = {
  value: number | undefined | null;
  best: number;
  worst: number;
  cuisineAverage: number | null;
  icon: React.ReactNode;
  title: string;
  className?: string;
  unit?: string;
  fixValueDecimal?: number;
  fixMinMaxDecimal?: number;
};

export default function BenchmarkCard({
  value,
  worst,
  best,
  icon,
  title,
  className = '',
  unit = '',
  cuisineAverage,
  fixValueDecimal = 2,
  fixMinMaxDecimal = 0,
}: Props) {
  const percentage = value ? (value - worst) / (best - worst) : 0;
  const angle = 180 * percentage;
  const hasValue = value !== null && value !== undefined;

  return (
    <Card className={className}>
      <div className="flex items-center gap-2 mb-6">
        {icon}
        <Typography className="font-semibold text-sm">{title}</Typography>
      </div>

      <div>
        {hasValue ? (
          <>
            <Typography className="text-2xl font-bold text-content">{`${value.toFixed(fixValueDecimal)} ${unit}`}</Typography>

            <div
              className={`line-clamp-1 text-xs font-medium text-tertiary mb-8 ${!cuisineAverage ? 'text-bg_tertiary' : ''}`}
            >
              {cuisineAverage
                ? `cuisine average of ${cuisineAverage} ${unit}`
                : '---'}
            </div>
          </>
        ) : (
          <>
            <Typography className="text-2xl font-bold text-content/50">
              No data
            </Typography>
            <div className="text-xs font-medium text-tertiary mb-8">
              Data not available for this metric
            </div>
          </>
        )}

        <div className="relative max-w-[200px] w-full h-[100px] mx-auto mt-6">
          <svg
            fill="rgb(0,0,0)"
            viewBox="0 0 100 50"
            className="overflow-visible rotate-[0deg] w-full h-full"
          >
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#5851B7" />
                <stop offset="50%" stopColor="#5493FF" />
                <stop offset="100%" stopColor="#00D8C7" />
              </linearGradient>
            </defs>
            <path
              fill="none"
              className="overflow-visible"
              stroke={hasValue ? 'url(#gradient)' : '#E5E7EB'}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="4"
              d="M 10 40 A 40 40 0 0 1 90 40"
            />
            {hasValue && (
              <>
                <line
                  x1="15"
                  y1="40"
                  x2="48"
                  y2="40"
                  className="transition-all duration-500"
                  transform={`rotate(${angle}, 50, 40)`}
                  strokeLinecap="round"
                  stroke={getNeedleColor(percentage)}
                  strokeWidth="2"
                />
                <circle
                  cx="50"
                  cy="40"
                  r="2"
                  fill={getNeedleColor(percentage)}
                  className="transition-all duration-500"
                />
              </>
            )}
          </svg>
          <div className=" absolute bottom-[-8px] left-[50%] -translate-x-[50%] flex justify-between w-full px-3.5 text-sm font-semibold text-card-foreground/50">
            <Typography>
              {hasValue ? worst.toFixed(fixMinMaxDecimal) : 'N/A'}
            </Typography>
            <Typography>
              {hasValue ? best.toFixed(fixMinMaxDecimal) : 'N/A'}
            </Typography>
          </div>
        </div>
      </div>
      <CardFooter />
    </Card>
  );
}
